/*
 * Description:
 * ---------------------
 * Every sentence the Changes screen shows (ChangesPanel), as pure functions
 * of what notabox-server answers at /api/v1/mesh/changes and /mesh/chain
 * (server/pkg/meshctl) and what each box says of its lines (/wans). A
 * change's own sentence and each site's are the server's; these words name
 * the setting with the console's names for traffic and lines, read it back
 * before it starts, and say how a box judged its trial from the record the
 * box signed. They never decide anything.
 *
 * A line is named the way its own box names it, so the same interface can
 * read differently at two sites; a sentence then names each site's lines
 * beside the site.
 *
 * ! file components/changeWords.js
 */
import { hm, list } from "./TrustFabricLayout";
import { cap } from "./approvalWords";
import { STATE_LABEL } from "./bondWords";
import { TIER } from "./EncryptionPanel";

const valid = (iso) => !!iso && !Number.isNaN(Date.parse(iso));
const midnight = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
/* When something happened, as a sentence says it: at 14:02, yesterday at
   14:02, on 27 Oct at 14:02. */
export function whenWords(iso, now = new Date()) {
  const d = new Date(iso);
  const days = Math.round((midnight(now) - midnight(d)) / 864e5);
  if (days === 0) return `at ${hm(iso)}`;
  if (days === 1) return `yesterday at ${hm(iso)}`;
  return `on ${d.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} at ${hm(iso)}`;
}
const its = (k, one, many) => (k === 1 ? one : many);

/* A server's or a box's sentence, as a sentence. A plain word first is
   capitalised; a site's or a line's name keeps the case it was given. */
export const sentence = (s) => {
  let t = String(s || "").trim();
  if (/^[a-z]+\b/.test(t) && !/^[a-z]+[-\d_.]/.test(t)) t = cap(t);
  return !t || /[.!?…”]$/.test(t) ? t : `${t}.`;
};

export const CHANGES_HERO = "One setting changed at your sites, one site at a time, each box trying it before it keeps it.";
export const LEAD =
  "Pick a setting and the sites to change it at. Weavian’s fleet agent asks each site’s box in turn; the box tries the change, " +
  "keeps it only if its lines do no worse, and takes it back by itself if they do. The first site that takes it back stops the change, " +
  "and every step is in that box’s signed record.";
export const NOT_ON_SERVER = "The server runs a build from before this screen shipped. It appears here once the server is updated; nothing else in the console is affected.";
export const NO_CUSTOMERS = "You see no organisation yet, so there is nothing here for you.";
export const NO_CHANGES = "No changes yet. Start one above and it appears here.";
export const NO_KINDS = "This server offers no changes yet.";
export const noSites = (name) => `None of ${name}’s sites can be changed yet.`;
export const adminsOnly = (name) => `Only ${name}’s administrators start or stop changes.`;
export const RECENT = "Recent changes";
export const START = "Start a change";
export const startAt = (name) => `Start a change at ${name}`;
export const BACK = "All changes";
export const SITES = "Site by site";

/* ── The setting ───────────────────────────────────────────────────────── */

/* A kind of traffic in a sentence: an app by its name, a category as its
   traffic. */
export function trafficWords(classes, key) {
  const c = (classes || []).find((x) => x.key === key);
  if (!c) return key || "";
  return c.isApp ? c.label : `${c.label} traffic`;
}

/* lineBook is what each box said of its lines (useBoxReads' answer for
   /wans): {node: {iface: name}}, null for a box that did not answer. */
export function lineBook(reads) {
  const out = {};
  for (const [node, r] of Object.entries(reads || {})) {
    out[node] = r?.body ? Object.fromEntries((r.body.wans || []).map((w) => [w.name, w.label || w.name])) : null;
  }
  return out;
}

/* A line at one site, by the name its box gave it, or its own. */
export const lineName = (book, node, iface) => book?.[node]?.[iface] || iface;

/* The sites grouped by what f says of each, in the sites' order. */
function grouped(sites, f) {
  const out = new Map();
  for (const s of sites) {
    const v = f(s);
    out.set(v, [...(out.get(v) || []), s]);
  }
  return [...out];
}

/* Parts as one run: a list, or one parted by semicolons where a part holds
   a list or a comma of its own. */
function run(parts) {
  if (!parts.some((p) => /,| and /.test(p))) return list(parts);
  return parts.length < 2 ? parts[0] || "" : `${parts.slice(0, -1).join("; ")}; and ${parts[parts.length - 1]}`;
}
/* Groups of sites as one run: "a, at x; b, at y; and c, at z". */
const atEach = (groups) => run(groups.map(([what, sites]) => `${what}, at ${list(sites)}`));

const NONE = "\u0000none";
const UNKNOWN = "\u0000unknown";
const nameOrNot = (book, node, iface) => (!book?.[node] ? UNKNOWN : iface in book[node] ? book[node][iface] : NONE);

/* linesAcross is every line the sites have, by interface: titled by its name
   where every site names it alike, else by the interface, with where it is
   called what. */
export function linesAcross(book, sites) {
  const ifaces = [...new Set(sites.flatMap((s) => Object.keys(book?.[s] || {})))].sort();
  return ifaces.map((iface) => {
    // The names first, then where it is not, then where nobody said.
    const rank = (v) => (v === NONE ? 1 : v === UNKNOWN ? 2 : 0);
    const groups = grouped(sites, (s) => nameOrNot(book, s, iface)).sort((x, y) => rank(x[0]) - rank(y[0]));
    if (groups.length === 1 && !rank(groups[0][0])) return { iface, title: groups[0][0], where: "" };
    const where = groups.map(([v, at]) => (v === NONE ? `not at ${list(at)}` : v === UNKNOWN ? `not known at ${list(at)}` : `${v} at ${list(at)}`));
    return { iface, title: iface, where: cap(where.join("; ")) };
  });
}

/* The lines of an order a site has, by its names; a box that did not say
   which lines it has keeps the order as written. */
const orderAt = (book, node, wans) => (book?.[node] ? wans.filter((w) => w in book[node]).map((w) => book[node][w]) : wans);
const sequence = (names) => (names.length === 1 ? `${names[0]} alone` : `${names[0]} first, then ${names.slice(1).join(", then ")}`);

/* The sites that have none of the lines an order names. */
export const withoutAny = (book, sites, wans) => sites.filter((s) => book?.[s] && orderAt(book, s, wans).length === 0);
/* The sites that do not have a line. */
export const without = (book, sites, iface) => sites.filter((s) => book?.[s] && !(iface in book[s]));

export const ACTIONS = {
  bond: { label: STATE_LABEL.bond, hint: "Spread across every working line at once." },
  failover: { label: STATE_LABEL.failover, hint: "One line at a time, in the order its lines are set; the next only when that one fails." },
};
export const SUITES = ["standard", "strong", "pq", "mobile", "pq-max", "off"];
export const suiteLabel = (id) => TIER[id]?.label || id;
export const suiteHint = (id) => (id === "off" ? "Weavian adds no layer; its apps keep their own protection." : TIER[id]?.blurb || "");

/* settingWords is what a change sets, as one sentence naming every site it
   reaches. now adds what a line is called before it is renamed. */
export function settingWords(entry, args, { classes, book, sites, now = false }) {
  const a = args || {};
  const traffic = trafficWords(classes, a.class);
  switch (entry) {
    case "class.reroute": {
      const wans = a.wans || [];
      const groups = grouped(sites.filter((s) => orderAt(book, s, wans).length > 0), (s) => sequence(orderAt(book, s, wans)));
      return groups.length ? `${cap(traffic)} uses ${atEach(groups)}.` : "";
    }
    case "class.action":
      if (a.action === "bond") return `${cap(traffic)} is bonded, spread across every working line at once, at ${list(sites)}.`;
      if (a.action === "failover") return `${cap(traffic)} fails over: it uses one line at a time, the next only when that one fails, at ${list(sites)}.`;
      return "";
    case "line.labels": {
      if (!a.iface) return "";
      const named = `The line ${a.iface} is called “${(a.label || "").trim()}” at ${list(sites)}.`;
      if (!now) return named;
      const was = grouped(sites, (s) => nameOrNot(book, s, a.iface)).filter(([v]) => v !== NONE && v !== UNKNOWN)
        .map(([v, at]) => `${v === a.iface ? "unnamed" : v} at ${list(at)}`);
      return was.length ? `${named} Now it is ${run(was)}.` : named;
    }
    case "class.encryption":
      if (!a.suite) return "";
      return a.suite === "off"
        ? `Weavian stops sealing ${traffic} at ${list(sites)}; its apps keep their own protection.`
        : `Weavian seals ${traffic} with ${suiteLabel(a.suite)} encryption at ${list(sites)}.`;
    default:
      return "";
  }
}

/* What still stops a change from starting, the first thing missing; empty
   when it can start. */
export function missing(kind, args, sites, book) {
  const a = args || {};
  if (!kind) return "Pick what to change.";
  if (!sites.length) return "Tick at least one site.";
  switch (kind.entry) {
    case "class.reroute": {
      if (!a.class) return "Pick which traffic.";
      if (!a.wans?.length) return "Add at least one line.";
      const nowhere = a.wans.filter((w) => sites.every((s) => book?.[s] && !(w in book[s])));
      if (nowhere.length) return `${list(nowhere)} ${its(nowhere.length, "is", "are")} at none of the sites you ticked. Take ${its(nowhere.length, "it", "them")} out.`;
      const bare = withoutAny(book, sites, a.wans);
      return bare.length ? `${list(bare)} ${its(bare.length, "has", "have")} none of these lines. Untick ${its(bare.length, "it", "them")}, or add one of ${its(bare.length, "its", "their")} lines.` : "";
    }
    case "class.action":
      if (!a.class) return "Pick which traffic.";
      return a.action ? "" : "Pick how it uses the lines.";
    case "line.labels": {
      if (!a.iface) return "Pick which line.";
      const bare = without(book, sites, a.iface);
      if (bare.length) return `${list(bare)} ${its(bare.length, "has", "have")} no line ${a.iface}. Untick ${its(bare.length, "it", "them")}.`;
      return a.label?.trim() ? "" : "Type its new name.";
    }
    case "class.encryption":
      if (!a.class) return "Pick which traffic.";
      return a.suite ? "" : "Pick how strongly to seal it.";
    default:
      return "This console cannot fill in that kind of change.";
  }
}

export const WHAT = "What to change";
export const WHERE = "At which sites";
export const TRAFFIC = "Which traffic";
export const PICK_TRAFFIC = "Pick a kind of traffic";
export const CATEGORIES = "Kinds of traffic";
export const APPS = "Apps";
export const ORDER = "Its lines, first choice first";
export const NO_ORDER = "No line yet: add the one it should use first.";
export const NOWHERE = "Not at any site you ticked";
export const NOT_USED = "Lines it does not use";
export const ADD = "Add";
export const addLine = (line) => `Add ${line}`;
export const moveUp = (line) => `Move ${line} up`;
export const moveDown = (line) => `Move ${line} down`;
export const takeOut = (line) => `Take ${line} out`;
export const TICK_FIRST = "Tick the sites first: their lines are listed here.";
export const READING_LINES = "Reading the lines at the sites you ticked…";
export const NO_LINES = "None of the sites you ticked said which lines it has.";
export const ACTION = "How it uses the lines";
export const LINE = "Which line";
export const NEW_NAME = "Its new name";
export const NAME_HINT = "For example Starlink";
export const SUITE = "How strongly to seal it";
export const APPROVAL = "Asks an approver first";
export const OFFLINE = "Not reporting now, so it cannot be changed.";
export const BEFORE_YOU_START = "Before you start";
export const START_NOW = "Start the change";
export const STARTING = "Starting…";
export const CANCEL = "Cancel";
export const HOW_IT_RUNS =
  "Weavian’s fleet agent makes it one site at a time. Each box tries it and keeps it only if its lines do no worse; " +
  "the first site that takes it back stops the change, and the sites after it stay as they are.";
export const APPROVAL_FIRST =
  "It asks an approver first: at each site, nothing changes until an approver says yes with their passkey, and a no stops the change.";

/* ── Where a change stands ─────────────────────────────────────────────── */

/* Who started a change: a person by name. */
export const startedBy = (b) => (typeof b === "string" ? b : b?.name || "someone");

export function siteState(s, now = new Date()) {
  switch (s.state) {
    case "waiting": return { tone: "idle", text: "Waits its turn" };
    case "approval": return { tone: "wait", text: "Waiting for an approver’s yes" };
    case "asking": return { tone: "busy", text: "Asking its box" };
    case "trying":
      if (valid(s.held_until) && Date.parse(s.held_until) > now.getTime()) return { tone: "wait", text: `On trial until ${hm(s.held_until)}` };
      return { tone: "wait", text: "Its trial is over: waiting for the box’s verdict" };
    case "kept": return { tone: "ok", text: "Kept" };
    case "taken_back": return { tone: "bad", text: "Taken back" };
    case "failed": return { tone: "bad", text: "Failed" };
    case "skipped": return { tone: "idle", text: "Skipped" };
    default: return { tone: "idle", text: cap(s.state || "") };
  }
}

const UNDER_WAY = ["approval", "asking", "trying"];
/* The site a running change is at now, if any. */
export const currentSite = (c) => (c.sites || []).find((s) => UNDER_WAY.includes(s.state));

/* changeState is a change as a whole, in a few words: where a running one
   is, where a stopped one stopped. */
export function changeState(c, now = new Date()) {
  const sites = c.sites || [];
  if (c.status === "running") {
    const cur = currentSite(c);
    const kept = sites.filter((s) => s.state === "kept").length;
    return { tone: cur ? siteState(cur, now).tone : "busy", text: kept ? `Under way: kept at ${kept} of ${sites.length} sites so far` : "Under way: not kept anywhere yet" };
  }
  const back = sites.find((s) => s.state === "taken_back");
  if (back) return { tone: "bad", text: `Taken back at ${back.node}` };
  const failed = sites.find((s) => s.state === "failed");
  if (failed) return { tone: "bad", text: `Failed at ${failed.node}` };
  if (c.status === "stopped") return { tone: "idle", text: valid(c.finished) ? `Stopped ${whenWords(c.finished, now)}` : "Stopped" };
  if (c.status === "failed") return { tone: "bad", text: "Did not go through" };
  if (c.status === "done" && sites.length && sites.every((s) => s.state === "kept")) return { tone: "ok", text: sites.length === 1 ? "Kept" : "Kept at every site" };
  return { tone: "idle", text: cap(c.status || "") };
}

export const started = (c, now = new Date()) =>
  `Started by ${startedBy(c.started_by)}${valid(c.started) ? ` ${whenWords(c.started, now)}` : ""}` +
  `${c.status !== "running" && valid(c.finished) ? `, ended ${whenWords(c.finished, now)}` : ""}.`;

/* A change's own sentence, else what kind of change it is. */
const headline = (c, kinds) => sentence(c.words || (kinds || []).find((k) => k.entry === c.entry)?.words || c.entry);

/* titleOf names a change by what it sets, in a few words; a kind this
   console does not know by the server's own sentence. */
export function titleOf(c, kinds, classes) {
  const a = c.args || {};
  const traffic = cap(trafficWords(classes, a.class));
  if (c.entry?.startsWith("class.") && !a.class) return headline(c, kinds);
  switch (c.entry) {
    case "class.reroute": return `The order of lines ${trafficWords(classes, a.class)} uses`;
    case "class.action":
      return a.action === "bond" ? `${traffic} bonded across the lines`
        : a.action === "failover" ? `${traffic} sent down one line at a time` : headline(c, kinds);
    case "line.labels": return a.iface ? `The line ${a.iface} named “${a.label}”` : headline(c, kinds);
    case "class.encryption": return a.suite === "off" ? `${traffic} no longer sealed` : `${traffic} sealed with ${suiteLabel(a.suite)} encryption`;
    default: return headline(c, kinds);
  }
}

/* The states whose reason the list shows: where a change stopped. */
export const STOPPED_AT = ["taken_back", "failed"];
export const siteOrder = (c) => ((c.sites || []).length > 1
  ? "Asked one at a time, in this order: each site waits until the one before kept the change."
  : "");

export const STOP = "Stop before the next site";
export const STOP_NOW = "Stop now";
export const KEEP_GOING = "Keep going";
export function askStop(c) {
  const cur = currentSite(c);
  const rest = (c.sites || []).filter((s) => s.state === "waiting").map((s) => s.node);
  const now = cur ? ` What is under way at ${cur.node} finishes as it would: its box keeps or takes back the change by itself.` : "";
  const left = rest.length ? ` ${list(rest)} ${its(rest.length, "is", "are")} not asked, and ${its(rest.length, "stays as it is", "stay as they are")}.` : "";
  return `Stop this change?${now}${left}`;
}

/* ── How a box judged its trial ────────────────────────────────────────── */

/* verdictOf is the verdict a site's box recorded for its change: the site's
   own when the server carries it, else the record that settles the change in
   a read of the box's records in the trace. Null until the box has judged. */
export function verdictOf(site, trace) {
  if (site.verdict) return site.verdict;
  if (!site.record) return null;
  let found = null;
  const walk = (hops) => (hops || []).forEach((h) => {
    if (h.capability === "changes" && h.node === site.node && h.status === "ok") {
      for (const r of h.result?.actions || []) {
        if ((r.change?.of === site.record || r.id === site.record) && r.change?.verdict) found = r.change.verdict;
      }
    }
    walk(h.hops);
  });
  walk(trace);
  return found;
}

/* The two controls a box judges a change against (cloud-anchor/pkg/change),
   what each holds a line the change acts on to, and how a line it failed
   did: its own fall, or how much worse it did than the lines it left alone
   did (beyond), which may have risen while it fell less, or rose less. */
const CONTROLS = {
  earlier: {
    title: "Against the same lines just before the change", than: "than just before the change",
    worse: (name, by) => `${name}’s health score fell ${points(by)} below the same lines just before the change`,
    by: (l) => l.fell_by,
  },
  concurrent: {
    title: "Against the lines it left alone", than: "than the lines it left alone",
    worse: (name, by) => `${name}’s health score did ${points(by)} worse than the lines it left alone`,
    by: (l) => l.beyond,
  },
};
const points = (x) => {
  const k = Math.round(Number(x) || 0);
  return `${k} ${its(k, "point", "points")}`;
};
const bare = (s) => String(s || "").trim().replace(/\.$/, "");

/* What a control found, in a sentence, of the worst line the change acts
   on; nameOf names a line at the site. */
export function controlWords(c, nameOf) {
  const words = CONTROLS[c.control];
  if (!words) return sentence(c.why);
  if (c.passed) return `The lines it changed did no worse ${words.than}.`;
  const by = (l) => Number(words.by(l)) || 0;
  const worst = (c.lines || []).filter((l) => l.touched).sort((x, y) => by(y) - by(x))[0];
  // A control a line failed by nothing it can name is said in the box's words.
  if (!worst || Math.round(by(worst)) <= 0) return sentence(c.why) || `The lines it changed did worse ${words.than}.`;
  return `${words.worse(nameOf(worst.line), by(worst))}.`;
}
export const controlTitle = (c) => `${CONTROLS[c.control]?.title || cap(c.control)}: ${c.passed ? "passed" : "failed"}`;

/* The controls a verdict failed, where its reason is their own words, as a
   box writes a reason from them; none where it took the change back for
   anything else, or kept it. */
function failedBy(v) {
  const failed = (v.controls || []).filter((c) => !c.passed);
  return bare(v.reason) === failed.map((c) => bare(c.why)).join("; ") ? failed : [];
}

/* verdictLead is the verdict in one sentence, from the box's record: the
   controls it passed, or each it failed; a change taken back for anything
   else, or recorded before the two controls, by the box's reason. */
export function verdictLead(v, nameOf) {
  if (!v) return "";
  if (v.result === "pass") {
    const thans = (v.controls || []).map((c) => CONTROLS[c.control]?.than).filter(Boolean);
    return thans.length ? `The box kept it: the lines it changed did no worse ${thans.join(", nor ")}.` : "The box kept it.";
  }
  const failed = failedBy(v);
  if (failed.length) return `The box took it back: ${failed.map((c) => bare(controlWords(c, nameOf))).join("; and ")}.`;
  return `The box took it back: ${bare(v.reason) || "it could not be judged"}.`;
}

/* The box's reason for its verdict, where its failed controls' own words do
   not already say it. */
export const ownReason = (v) => (v?.reason && !failedBy(v).length ? v.reason : "");

/* The states whose own words say all a server's sentence would: who a site
   waits for, and until when its trial runs. */
const SAID_BY_STATE = ["approval", "trying"];

/* siteWhy is the sentence a site's row gives beside where it stands: the
   verdict its box recorded, in the console's words, where that verdict is
   what settled it; else the server's sentence, which may hold the box's own
   words and its interface names, unless the state already says it. */
export function siteWhy(s, verdict, nameOf) {
  const judged = verdict && (s.state === "kept" ? verdict.result === "pass" : s.state === "taken_back" && verdict.result !== "pass");
  if (judged) return verdictLead(verdict, nameOf);
  return SAID_BY_STATE.includes(s.state) ? "" : sentence(s.why);
}

export const HOW_JUDGED = "How the box judged it";
export const JUDGED_LEAD = "Each line’s health score out of 100, as the box’s own probes measured it, before the change and during its trial.";
/* A control's table: each line's scores and its own change, and under the
   concurrent control how each line the change acts on fared against the
   lines it left alone. The two scores share a column, so the table fits a
   phone. */
const AGAINST = "Against the lines left alone";
export const columnsOf = (c) => ["Line", "Before → during", "Its own change", ...(c.control === "concurrent" ? [AGAINST] : [])];
export const AGAINST_NOTE = `${AGAINST} is how many points worse, or better, each line the change acts on did than the lines it left alone did on average.`;
const score = (x) => (Number.isFinite(Number(x)) ? String(Math.round(Number(x))) : "");
export const scores = (l) => `${score(l.before)} → ${score(l.during)}`;
/* A line's own change in points: a fall as a fall, a rise as a rise. */
export function ownChange(fell) {
  const k = Math.round(Number(fell) || 0);
  return k > 0 ? `fell ${k}` : k < 0 ? `rose ${-k}` : "no change";
}
/* How a line the change acts on fared against the lines it left alone, or
   that it is one of those. */
export function against(l) {
  if (!l.touched) return "left alone";
  const k = Math.round(Number(l.beyond) || 0);
  return k > 0 ? `${k} worse` : k < 0 ? `${-k} better` : "the same";
}
export const boxWords = (s) => (s ? `In the box’s own words: “${String(s).trim()}”` : "");

/* ── What it would do (the supervisor's prediction) ────────────────────── */

/* The supervisor (server/pkg/meshctl supervisor.go) works out what a change
   would do before it starts: each site's lines now and after, the load moved
   at each gateway, what it warns of, and whether the change needs the
   person's yes. Its own sentences name interfaces as the boxes do and write a
   kind of traffic as "<ID> traffic"; here each kind is named as the console
   names it, and what moves where and what each line would carry are said
   again with the console's names for lines. */

export const PREDICTING = "Reading each site’s traffic for half a minute to see what this change would do…";
export const WOULD_DO = "What it would do";
export const EXPECTED = "What it was expected to do";
export const NEEDS_YES = "It needs your yes before it starts";
export const YES_BOX = "I have read what it would do, and want it to start.";
export const START_WITH_YES = "Start it with my yes";
export const NUMBERS = "The numbers behind it";
export const NUMBERS_LEAD =
  "What each box sent on each of its lines, in megabits a second, from two readings half a minute apart, and what the change would put there. " +
  "A line’s capacity is its box’s own estimate of what it can send: measured where the box saw the line fill, else the most the line has been seen to carry.";
export const LINE_COLUMNS = ["Line", "Now → after", "What it can carry"];
export const WATCH = "This week across your sites";

const rounded = (x) => {
  const n = Number(x) || 0;
  return Math.round(n * 10) / 10 < 10 ? String(Math.round(n * 10) / 10) : String(Math.round(n));
};
/* A rate as the supervisor's sentences say it. */
export const mbit = (x) => (Number(x) > 0 && Number(x) < 0.05 ? "under 0.1 Mbit/s" : `${rounded(x)} Mbit/s`);
/* named is a server's sentence with each kind of traffic it writes as
   "<ID> traffic" named as the console names it. */
export const named = (s, classes) => String(s || "").replace(/\b([A-Z][A-Z0-9_]*) traffic\b/g,
  (all, key) => ((classes || []).some((c) => c.key === key) ? trafficWords(classes, key) : all));
const percent = (x) => `${Math.abs(Number(x)) >= 10 ? Math.round(Number(x)) : Math.round(Number(x) * 10) / 10}%`;
const fewSites = (sites) => (sites.length > 3 ? `${sites.length} sites` : list(sites));
const eachMoved = (each) => {
  const lo = Math.min(...each);
  const hi = Math.max(...each);
  return rounded(lo) === rounded(hi) ? `about ${mbit(lo)} at each` : `${rounded(lo)} to ${mbit(hi)} at each`;
};

/* foresightWords is what a prediction says the change does, in sentences:
   what moves onto which line at which sites, by the console's names; then
   the server's own sentences on what moves nowhere, the sites it could not
   read, and the gateways. A change it predicts nothing of, as one naming a
   line, by the server's words alone. */
export function foresightWords(p, { classes, book }) {
  const sites = p.sites || [];
  const words = (p.words || []).map((w) => named(w, classes));
  if (!sites.length) return words;
  const moving = sites.filter((s) => !s.unread && !s.still && s.moved_mbps > 0);
  if (!moving.length) return words;
  const traffic = trafficWords(classes, p.args?.class);
  const parts = grouped(moving, (s) => (s.to ? lineName(book, s.node, s.to) : "")).map(([to, at], i) => {
    const sum = at.reduce((n, s) => n + s.moved_mbps, 0);
    const where = fewSites(at.map((s) => s.node));
    let part = `about ${mbit(sum)}${i === 0 ? ` of ${traffic}` : ""}`;
    part += to ? ` onto ${to} at ${where}` : ` onto other lines at ${where}, spread as each box’s bond shares its lines`;
    return at.length > 1 ? `${part}, ${eachMoved(at.map((s) => s.moved_mbps))}` : part;
  });
  return [`It moves ${parts.join("; and ")}.`, ...words.slice(1)];
}

/* The line a warning names, as the prediction read it. */
const warnedLine = (p, w) => (p.sites || []).find((s) => s.node === w.site)?.lines?.find((l) => l.name === w.line);
const fill = (l) => percent((100 * l.after_mbps) / l.capacity_mbps);

/* warningWords is one thing a prediction warns of, in a sentence naming the
   line as its box does; a gateway's and the week's in the server's words. */
export function warningWords(p, w, nameOf, classes) {
  const l = warnedLine(p, w);
  if (!l) return named(w.words, classes);
  const at = `${nameOf(w.site, w.line)} at ${w.site} would carry about ${mbit(l.after_mbps)}`;
  switch (w.kind) {
    case "over":
    case "full":
      return `${at}, ${fill(l)} of the ${mbit(l.capacity_mbps)} it was measured to carry.`;
    case "unproven":
      return l.capacity_mbps > 0
        ? `${at}, more than the ${mbit(l.capacity_mbps)} it has been seen to carry; what it can carry has not been measured.`
        : `${at}, and nothing is known yet of what it can carry.`;
    case "weak":
      return `${at}, within what it has been seen to carry, though its box’s estimate of what it can carry is weak.`;
    default:
      return named(w.words, classes);
  }
}

/* yesWhy is why a change needs the person's yes, in a sentence: each line it
   would run past what the line was measured to carry, the only thing that
   asks one. */
export function yesWhy(p, nameOf) {
  const why = (p.warnings || []).filter((w) => w.needs_yes).map((w) => {
    const l = warnedLine(p, w);
    return l ? `${nameOf(w.site, w.line)} at ${w.site} would carry ${fill(l)} of what it was measured to carry` : "";
  }).filter(Boolean);
  return `${sentence(why.length ? list(why) : p.needs_yes || "")} Nothing stops a change like this one; your yes says you have read what it would do.`;
}

/* yesBy is who said yes to a change that needed it, and when. */
export const yesBy = (y, now = new Date()) => `${startedBy(y.by)} said yes to it ${whenWords(y.at, now)}, having read why it needed one.`;

/* What each line can carry, as its box knows it. */
export function capacityOf(l) {
  if (l.evidence === "measured") return `${mbit(l.capacity_mbps)}, measured`;
  if (l.evidence === "carried") return `has carried ${mbit(l.capacity_mbps)}`;
  return "not known";
}
export const nowAfter = (l) => `${rounded(l.now_mbps)} → ${mbit(l.after_mbps)}`;
export const behind = (gateway) => (gateway ? `behind the gateway ${gateway}` : "");

/* gatewayWords is one gateway the change reaches, in a sentence: what it
   moves between its sites' lines and how busy the gateway is. */
export function gatewayWords(g) {
  const moved = g.moved_mbps > 0 ? `about ${mbit(g.moved_mbps)} moved between the lines of ${fewSites(g.sites || [])}` : `nothing moved at ${fewSites(g.sites || [])}`;
  if (g.unread) return `${g.name}: ${moved}; its load could not be read.`;
  const busy = `its processor ${Math.round(g.busy_pct)}% busy over its last minute${g.instance_type ? ` (${g.instance_type})` : ""}`;
  return `${g.name}: ${moved}; ${busy}${g.past ? `, past its headroom: ${bare(g.past)}` : ""}.`;
}

/* trialWords is how a site's prediction held against its trial. */
export function trialWords(t) {
  if (!t) return "";
  if (t.why) return `The prediction could not be held against its trial: ${bare(t.why)}.`;
  return `The prediction held to within ${percent(t.error_pct)}: that much of this site’s traffic was on another line than predicted, ` +
    `against ${percent(t.baseline_pct)} had nothing moved.`;
}

/* accuracyWords is how close the predictions of one kind came. */
export function accuracyWords(a, kinds) {
  const kind = (kinds || []).find((k) => k.entry === a.entry);
  return `Predictions of ${kind ? kind.words : a.entry}: over ${a.trials} ${its(a.trials, "trial", "trials")}, ` +
    `${percent(a.error_pct)} of a site’s traffic was on another line than predicted, on average, against ${percent(a.baseline_pct)} had nothing moved.`;
}

/* The week's warnings, every sentence of its words that is one. */
export const watchWarnings = (w) => new Set([
  ...(w?.gateways || []).flatMap((g) => g.warnings || []),
  ...(w?.classes || []).flatMap((c) => c.warnings || []),
  ...(w?.drift || []).map((d) => d.warning).filter(Boolean),
]);

/* ── Weavian's agents at this customer ─────────────────────────────────── */

/* Weavian's agents at one customer (server/pkg/meshctl chain.go) are off,
   watching or acting. A customer's administrators switch them between off
   and watching; Weavian's staff alone let them act, which the server
   requires too. */
export const AGENTS = "Agents";
export const MODES = ["off", "watch", "act"];
export const agentsAre = (mode, name) => ({
  off: `Weavian’s agents are off at ${name}: they read nothing there and change nothing.`,
  watch: `Weavian’s agents are watching ${name}: when one of its lines loses ground, they say what they would do about it, and change nothing.`,
  act: `Weavian’s agents are acting at ${name}: when a line loses ground they change settings at its sites, and each box keeps a change only if its lines do no worse.`,
}[mode] || `Weavian’s agents are ${mode} at ${name}.`);
/* What letting the agents act means, which Weavian's staff allow in the
   page before it is set. */
export const actingMeans = (name) =>
  `Weavian’s agents will change settings at ${name}’s sites when a line loses ground: each box tries the change, keeps it only if its lines do no worse, ` +
  "and takes it back by itself; a change that would push a line past what it was measured to carry waits for a person.";
export const ALLOW_ACTING = "Let Weavian’s agents act";
export const ALLOW = "Allow";
export const WATCH_ONLY = "Switched on, they only watch: they say what they would do, and change nothing.";
export const agentsSwitch = (name) => `Weavian’s agents at ${name}`;
export const modeWord = (mode) => ({ off: "Off", watch: "Watching", act: "Acting" }[mode] || cap(mode || ""));
export const switchedBy = (c, now = new Date()) =>
  `${c.mode === "off" ? "Switched off" : "Switched on"} by ${c.by}${valid(c.at) ? ` ${whenWords(c.at, now)}` : ""}.`;
export const agentsAdminsOnly = (name) => `Only ${name}’s administrators switch them on or off.`;
export const AGENT_RUNS = "What they found lately";
export const NO_RUNS = "Nothing found yet.";
export const foundWhen = (r, now = new Date()) => `Found ${whenWords(r.started, now)}.`;

/* agentWords is one of the agents' sentences with each kind of traffic it
   names by its ID, as "REALTIME traffic" or REALTIME alone, named as the
   console names it. */
const agentWords = (s, classes) => named(s, classes).replace(/\b([A-Z][A-Z0-9_]*)\b/g,
  (all, key) => ((classes || []).some((c) => c.key === key) ? trafficWords(classes, key) : all));

/* proposedWords is what the agents proposed for a finding, in their own
   words: the change they would make, or why none of their remedies would
   help. */
export const proposedWords = (p, classes) =>
  agentWords(sentence(p?.effect?.words ? `Proposed: ${p.effect.words}` : p?.why), classes);

/* runMade is whether the agents made what they proposed, and the outcome of
   a change they made: under way, or where it ended, as a change's state is
   said (kept, or taken back where it was). A run that could not make it says
   why in the server's sentence; one that only watched, or found nothing to
   do, is simply not made. */
export function runMade(r, now = new Date()) {
  const p = r.proposal || {};
  if (p.made) {
    const c = r.change;
    if (!c || c.status === "running") return { tone: c ? changeState(c, now).tone : "busy", text: "Made: under way" };
    const st = changeState(c, now);
    return { tone: st.tone, text: `Made: ${st.text.charAt(0).toLowerCase()}${st.text.slice(1)}` };
  }
  if (p.for_person) return { tone: "wait", text: "Not made: it needs a person’s yes, which the agents never give" };
  if (r.status === "failed" && p.not_made) return { tone: "bad", text: `Not made: ${bare(p.not_made)}` };
  return { tone: "idle", text: "Not made" };
}
